import {Component, inject, effect, computed, signal, OnInit} from '@angular/core';
import {TrajetSelectionneService} from '../../gestion-navette/onglets-gestion-navette/trajets/services/trajet-selectionne.service';
import {StatutTrajetEnum} from '../../gestion-navette/onglets-gestion-navette/trajets/model/statut-trajet.enum';
import {AlertService, ConfirmationModalService, ConfirmationData, ConfirmationAction} from 'sigale-angular';
import {TranslateService} from '@ngx-translate/core';
import {RafraichissementTrajetsService} from '../../gestion-navette/onglets-gestion-navette/trajets/services/rafraichissement-trajets.service';
import {finalize} from 'rxjs/operators';
import {MessageService} from 'primeng/api';
import {TrajetService} from '../../shared/services/trajet.service';
import {ParametrageService} from '../../shared/services/parametrage.service';

@Component({
	selector: 'app-detail-reservation-trajet-dialog',
	templateUrl: './detail-reservation-trajet-dialog.component.html',
	styleUrl: './detail-reservation-trajet-dialog.component.scss'
})
export class DetailReservationTrajetDialogComponent implements OnInit {
	// Constantes
	readonly NOM_ALERTE_DETAIL_RESERVATION = 'alerteDetailReservationTrajet';

	// Injects
	private alertService = inject(AlertService);
	private confirmationModalService = inject(ConfirmationModalService);
	private messageService = inject(MessageService);
	private parametrageService = inject(ParametrageService);
	private rafraichissementTrajetsService = inject(RafraichissementTrajetsService);
	private trajetSelectionneService = inject(TrajetSelectionneService);
	private trajetService = inject(TrajetService);
	private translateService = inject(TranslateService);

	// Signaux
	actionEnCours = signal<boolean>(false);
	delaiLimiteAnnulationReservationTrajet = signal<number>(0);
	visible = signal<boolean>(false);

	// Variables
	protected readonly StatutTrajetEnum = StatutTrajetEnum;

	// Computed
	estTrajetAnnulable = computed(() => this.estTrajetReserve() && Date.now() < new Date(this.trajet()?.dateDepart).getTime() - this.delaiLimiteAnnulationReservationTrajet() * 60 * 1000);
	estTrajetComplet = computed(() => this.trajet()?.statut === StatutTrajetEnum.COMPLET);
	estTrajetReservable = computed(() => !this.estTrajetReserve() && this.trajet()?.statut !== StatutTrajetEnum.ANNULE);
	estTrajetReserve = computed(() => !!this.trajet()?.reservationId);
	statutTrajet = computed(() => this.estTrajetReserve() ? StatutTrajetEnum.RESERVE : this.trajet()?.statut);
	trajet = computed(() => this.trajetSelectionneService.trajetSelectionne());

	constructor() {
		this.ecouterChangementsTrajetSelectionne();
	}

	ngOnInit() {
		this.recupererDelaiLimiteAnnulationReservationTrajet();
	}

	/**
	 * Écoute les changements de trajet sélectionné dans le service et affiche une confirmation si un trajet est sélectionné.
	 */
	ecouterChangementsTrajetSelectionne(): void {
		effect(() => {
			if (this.trajetSelectionneService.trajetSelectionne()) {
				this.visible.set(true);
			}
		}, { allowSignalWrites: true });
	}

	/**
	 * Récupère le délai limite d'annulation d'une réservation de trajet avant son départ, en minutes.
	 */
	recupererDelaiLimiteAnnulationReservationTrajet(): void {
		this.parametrageService.getDelaiLimiteAnnulationReservationTrajet().subscribe({
			next: (delai: number) => {
				this.delaiLimiteAnnulationReservationTrajet.set(delai);
			}
		});
	}

	/**
	 * Désélectionne le trajet dans le service.
	 */
	deselectionnerTrajet(): void {
		this.trajetSelectionneService.deselectionnerTrajet();
	}

	/**
	 * Nettoie l'erreur en cours.
	 */
	nettoyerErreur(): void {
		this.alertService.clear(this.NOM_ALERTE_DETAIL_RESERVATION);
	}

	/**
	 * Réinitialise l'état du détail de réservation du trajet (désélection et nettoyage des erreurs).
	 */
	reinitialiserDetailReservationTrajet(): void {
		this.deselectionnerTrajet();
		this.nettoyerErreur();
	}

	/**
	 * Ferme le dialog de détail de réservation de trajet.
	 */
	fermerDialogDetailReservationTrajet(): void {
		this.visible.set(false);
	}

	/**
	 * Réserve le trajet sélectionné en appelant le service de réservation et gère la réponse.
	 */
	reserverTrajet(): void {
		this.actionEnCours.set(true);
		this.alertService.clear(this.NOM_ALERTE_DETAIL_RESERVATION);
		this.trajetService.reserverTrajet(this.trajet().id).pipe(
			finalize(() => {
				this.actionEnCours.set(false);
				this.rafraichissementTrajetsService.demanderRafraichissement();
			})
		).subscribe({
			next: () => {
				this.messageService.add({ severity: 'success', detail: this.translateService.instant('RESERVATION_NAVETTE.MESSAGE_RESERVATION_SUCCES') });
				this.deselectionnerTrajet();
				this.fermerDialogDetailReservationTrajet();
			},
			error: (erreur) => {
				this.alertService.error(erreur.error, this.NOM_ALERTE_DETAIL_RESERVATION);
			}
		});
	}

	/**
	 * Affiche la popup de confirmation pour la réservation du trajet.
	 */
	afficherPopupConfirmationReservation(): void {
		const actionConfirmer = new ConfirmationAction(
			this.translateService.instant('RESERVATION_NAVETTE.DETAIL.BTN_RESERVER')
		);
		const actionAnnuler = new ConfirmationAction(
			this.translateService.instant('GLOBAL.BTN_FERMER'),
			'link'
		);

		actionConfirmer.onClick.subscribe(() => {
			this.reserverTrajet();
			this.confirmationModalService.fermer();
		});

		actionAnnuler.onClick.subscribe(() => {
			this.confirmationModalService.fermer();
		});

		this.confirmationModalService.ouvrir(new ConfirmationData(
			this.translateService.instant('RESERVATION_NAVETTE.RESERVATION.TITRE'),
			this.translateService.instant('RESERVATION_NAVETTE.RESERVATION.MESSAGE'),
			[actionConfirmer, actionAnnuler]
		));
	}

	/**
	 * Annule la réservation pour le trajet sélectionné en appelant le service de réservation et gère la réponse.
	 */
	annulerReservationTrajet(): void {
		this.actionEnCours.set(true);
		this.trajetService.annulerReservationTrajet(this.trajet().id, this.trajet().reservationId).pipe(
			finalize(() => {
				this.actionEnCours.set(false);
			})
		).subscribe({
			next: () => {
				this.rafraichissementTrajetsService.demanderRafraichissement();
				this.fermerDialogDetailReservationTrajet();
				this.deselectionnerTrajet();
				this.messageService.add({ severity: 'success', detail: this.translateService.instant('RESERVATION_NAVETTE.ANNULATION.MESSAGE_ANNULATION_SUCCES') });
			},
			error: (erreur) => {
				this.alertService.error(erreur.error, this.NOM_ALERTE_DETAIL_RESERVATION);
			}
		});
	}

	/**
	 * Affiche la popup de confirmation pour l'annulation d'une réservation du trajet.
	 */
	afficherPopupConfirmationAnnulation(): void {
		const actionConfirmer = new ConfirmationAction(
			this.translateService.instant('RESERVATION_NAVETTE.ANNULATION.BTN_ANNULER_RESERVATION')
		);
		const actionFermer = new ConfirmationAction(
			this.translateService.instant('GLOBAL.BTN_FERMER'),
			'link'
		);

		actionConfirmer.onClick.subscribe(() => {
			this.annulerReservationTrajet();
			this.confirmationModalService.fermer();
		});

		actionFermer.onClick.subscribe(() => {
			this.confirmationModalService.fermer();
		});

		this.confirmationModalService.ouvrir(new ConfirmationData(
			this.translateService.instant('RESERVATION_NAVETTE.ANNULATION.TITRE'),
			this.translateService.instant('RESERVATION_NAVETTE.ANNULATION.MESSAGE'),
			[actionConfirmer, actionFermer]
		));
	}
}
